import { loanApplicationSignalRConnection } from 'app/services/signalR';
import _ from 'lodash';
import { nanoid } from 'nanoid';

import {
  PRESENCE_ACTION_NAME_PREFIX,
  PresenceEntityType,
  PresenceSignalRMessageType,
} from 'constants/presence';
import { jsonStringify } from 'utils/json';
import { createNamedActions } from 'utils/namedActionTypesProxy';
import { buildPresenceEntityRef } from 'utils/presence';

export const PRESENCE_ACTION_TYPE = createNamedActions(PRESENCE_ACTION_NAME_PREFIX, {
  SET_PRESENCE_SNAPSHOT: 'SET_PRESENCE_SNAPSHOT',
  USER_JOINED: 'USER_JOINED',
  USER_LEFT: 'USER_LEFT',
  USER_LOCATION_CHANGED: 'USER_LOCATION_CHANGED',
  USER_STATUS_CHANGED: 'USER_STATUS_CHANGED',
  FIELD_LOCK_ACQUIRED: 'FIELD_LOCK_ACQUIRED',
  FIELD_LOCK_RELEASED: 'FIELD_LOCK_RELEASED',
  FIELD_LOCK_DENIED: 'FIELD_LOCK_DENIED',
  FIELD_VALUE_CHANGING: 'FIELD_VALUE_CHANGING',
  FIELD_VALUE_COMMITTED: 'FIELD_VALUE_COMMITTED',
  FIELD_VALUES_REVERTED: 'FIELD_VALUES_REVERTED',
  TRACK_LOCAL_FIELD_EDIT: 'TRACK_LOCAL_FIELD_EDIT',
  CLEAR_LOCAL_EDITED_FIELDS: 'CLEAR_LOCAL_EDITED_FIELDS',
  SET_PENDING_FIELD_VALUES: 'SET_PENDING_FIELD_VALUES',
  SET_IS_CONNECTED: 'SET_IS_CONNECTED',
  DISMISS_NOTIFICATION: 'DISMISS_NOTIFICATION',
  START_FOLLOWING: 'START_FOLLOWING',
  STOP_FOLLOWING: 'STOP_FOLLOWING',
  SET_FOLLOW_NAVIGATING: 'SET_FOLLOW_NAVIGATING',
  CLEAR_REMOTE_FIELD_VALUES: 'CLEAR_REMOTE_FIELD_VALUES',
  CLEAR_PRESENCE_STATE: 'CLEAR_PRESENCE_STATE',
  RESET_PRESENCE: 'RESET_PRESENCE',
});

export const setPresenceSnapshotAction = payload => ({
  type: PRESENCE_ACTION_TYPE.SET_PRESENCE_SNAPSHOT,
  payload,
});

export const userJoinedAction = payload => ({
  type: PRESENCE_ACTION_TYPE.USER_JOINED,
  payload: { ...payload, notificationId: nanoid(), occurredAt: Date.now() },
});

export const userLeftAction = payload => ({
  type: PRESENCE_ACTION_TYPE.USER_LEFT,
  payload: { ...payload, notificationId: nanoid(), occurredAt: Date.now() },
});

export const userLocationChangedAction = payload => ({
  type: PRESENCE_ACTION_TYPE.USER_LOCATION_CHANGED,
  payload,
});

export const userStatusChangedAction = payload => ({
  type: PRESENCE_ACTION_TYPE.USER_STATUS_CHANGED,
  payload,
});

export const fieldLockAcquiredAction = payload => ({
  type: PRESENCE_ACTION_TYPE.FIELD_LOCK_ACQUIRED,
  payload,
});

export const fieldLockReleasedAction = payload => ({
  type: PRESENCE_ACTION_TYPE.FIELD_LOCK_RELEASED,
  payload,
});

export const fieldLockDeniedAction = payload => ({
  type: PRESENCE_ACTION_TYPE.FIELD_LOCK_DENIED,
  payload,
});

export const fieldValueChangingAction = payload => ({
  type: PRESENCE_ACTION_TYPE.FIELD_VALUE_CHANGING,
  payload,
});

export const fieldValueCommittedAction = payload => ({
  type: PRESENCE_ACTION_TYPE.FIELD_VALUE_COMMITTED,
  payload,
});

export const fieldValuesRevertedAction = payload => ({
  type: PRESENCE_ACTION_TYPE.FIELD_VALUES_REVERTED,
  payload,
});

export const trackLocalFieldEditAction = fieldPath => ({
  type: PRESENCE_ACTION_TYPE.TRACK_LOCAL_FIELD_EDIT,
  payload: fieldPath,
});

export const clearLocalEditedFieldsAction = () => ({
  type: PRESENCE_ACTION_TYPE.CLEAR_LOCAL_EDITED_FIELDS,
});

export const setPendingFieldValuesAction = payload => ({
  type: PRESENCE_ACTION_TYPE.SET_PENDING_FIELD_VALUES,
  payload,
});

export const setPresenceConnectedAction = payload => ({
  type: PRESENCE_ACTION_TYPE.SET_IS_CONNECTED,
  payload,
});

export const dismissPresenceNotificationAction = notificationId => ({
  type: PRESENCE_ACTION_TYPE.DISMISS_NOTIFICATION,
  payload: notificationId,
});

export const startFollowingAction = ({ userId, connectionId }) => ({
  type: PRESENCE_ACTION_TYPE.START_FOLLOWING,
  payload: { userId, connectionId },
});

export const stopFollowingAction = () => ({
  type: PRESENCE_ACTION_TYPE.STOP_FOLLOWING,
});

export const setFollowNavigatingAction = payload => ({
  type: PRESENCE_ACTION_TYPE.SET_FOLLOW_NAVIGATING,
  payload,
});

const followNavigationFlag = { current: false };

export const getIsFollowNavigationInProgress = () => followNavigationFlag.current;

export const setFollowNavigationInProgress = value => {
  followNavigationFlag.current = value;
};

export const clearRemoteFieldValuesAction = ({ reason } = {}) => ({
  type: PRESENCE_ACTION_TYPE.CLEAR_REMOTE_FIELD_VALUES,
  payload: { reason },
});

export const clearPresenceStateAction = () => ({
  type: PRESENCE_ACTION_TYPE.CLEAR_PRESENCE_STATE,
});

export const resetPresenceAction = () => ({
  type: PRESENCE_ACTION_TYPE.RESET_PRESENCE,
});

export const updateLocationAction =
  ({ entityType = PresenceEntityType.LOAN, entityId, metadata }) =>
  dispatch => {
    const wireEntityId = buildPresenceEntityRef(entityType, entityId);

    if (_.isNil(wireEntityId)) {
      return;
    }

    const { connectionId } = loanApplicationSignalRConnection;

    if (!_.isNil(connectionId)) {
      dispatch(userLocationChangedAction({ connectionId, metadata: metadata ?? null }));
    }

    loanApplicationSignalRConnection
      .invoke(
        PresenceSignalRMessageType.UPDATE_LOCATION,
        wireEntityId,
        !_.isNil(metadata) ? jsonStringify(metadata) : null,
      )
      .catch(_.noop);
  };
